/**
 * ThemeProvider de NeoCore (ADR 014). LIGHT por defecto durante la transición:
 * el shell nuevo (shadcn) ya soporta dark, pero las pantallas de contenido aún
 * usan tokens claros — hasta migrarlas (F2+), dark deja el admin "mitad y mitad".
 * Por eso arrancamos claro y parejo; el toggle sigue disponible. Al migrar las
 * páginas, volvemos el default a 'dark'.
 * Persiste la preferencia y aplica la clase `.dark`/`.light` en <html>.
 */
import { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'dark' | 'light' | 'system'
type ThemeState = { theme: Theme; setTheme: (t: Theme) => void; toggle: () => void }

// v2: bump para descartar el 'dark' que quedó guardado en la primera versión del
// shell (evita que alguien siga viendo el estado mitad-dark al recargar).
const STORAGE_KEY = 'neocore-theme-v2'
const ThemeContext = createContext<ThemeState | null>(null)

function resolve(theme: Theme): 'dark' | 'light' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return theme
}

export function ThemeProvider({ children, defaultTheme = 'light' }: { children: React.ReactNode; defaultTheme?: Theme }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return defaultTheme
    return (localStorage.getItem(STORAGE_KEY) as Theme) || defaultTheme
  })

  useEffect(() => {
    const root = document.documentElement
    const applied = resolve(theme)
    root.classList.remove('light', 'dark')
    root.classList.add(applied)
    // Nota: NO seteamos root.style.colorScheme global durante la transición —
    // así los controles nativos de las pantallas viejas (light) no se oscurecen.
    // El shell nuevo aplica color-scheme dark en su propio contenedor (F1).
  }, [theme])

  const setTheme = (t: Theme) => {
    localStorage.setItem(STORAGE_KEY, t)
    setThemeState(t)
  }
  const toggle = () => setTheme(resolve(theme) === 'dark' ? 'light' : 'dark')

  return <ThemeContext.Provider value={{ theme, setTheme, toggle }}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme debe usarse dentro de ThemeProvider')
  return ctx
}
